<template>

    <v-layout column fill-height class="mainPanel">
        <v-layout row align-center v-if="records || params.filters.length" class="chips-and-toggle-row">
            <div class="chips-wrapper">
                <span class="chips-label mr-2" v-show="params.filters.length">Filters Applied:</span>
                <active-filter-chip
                    v-for="(filter, i) in params.filters"
                    :key="i + '-' + filter.id"
                    :filter="filter"
                />
            </div>
            <v-layout shrink justify-end align-center class="view-toggle-row">
                <toggle-button
                    :items="viewToggleItems"
                    v-model="viewMode"
                    class="students3d-toggle-btn"
                />
            </v-layout>
        </v-layout>

        <!-- Empty State -->
        <fe-overlay
            v-if="!records"
            :absolute="false"
            :header="headline"
            :text="subheading"
            :image="rocketImg"
            imageSize="60%"
            color="white"
        />

        <!-- Student Chips -->
        <renderer ref="rendererRef" v-show="records && !showGrid"
            @labelHover="labelHover = $event"
            @select="selectedStudents = $event"
            style="position:relative;"
        >
            <student-card />

            <v-card dark v-if="labelHover"
                :style="{
                    position: 'absolute',
                    left: `${labelHover.x}px`,
                    top: `${labelHover.y}px`,
                    transform: 'translate(-50%, 0)',
                    padding: '1px 6px 1px 6px',
                    pointerEvents: 'none'
                }"
            >
                {{labelHover.text}}
            </v-card>
        </renderer>

        <!-- Student Grid -->
        <div v-if="records && showGrid" style="height:100%; padding:16px;">
            <grid rowGroup />
        </div>

        <fe-dialog
            v-if="!$_.isEmpty(selectedStudents)"
            title="Selected Students"
            @close="selectedStudents=[]"
            width="80%"
            persistent
        >
            <div style="height:500px;">
                <grid selected-only />
            </div>
        </fe-dialog>


    </v-layout>
</template>

<script>
import rocketImg from '@/assets/images/rocket.png'
import Group from '../Group'
import { mapState } from 'vuex'
import { mapLocalState } from '@/util/vuexHelper'
import ActiveFilterChip from './ActiveFilterChip'
import Renderer from './Renderer'
import Grid from './Grid'
import StudentCard from './StudentCard'
import ToggleButton from '@/components/common/button/ToggleButton'
import * as NlfAgentProcessor from '../NlfAgentProcessor'
import { isDemographicFilterType } from '../Util'
import FilterPanelUtil from '@/components/modules/students3d/FilterPanelUtil'

export default {
    name: 'MainPanel',
    inject: ['localStore'],

    components: {
        ActiveFilterChip,
        Renderer,
        Grid,
        StudentCard,
        ToggleButton,
    },

    computed: {
        ...mapState('global', ['sessionUser']),
        ...mapLocalState([
            'params', 'charts', 'activeStudent', 'selectedStudents', 'zoomed',
            'activeGrouping', 'activeLayout', 'groupers',
            'aiModeEnabled', 'chatMessages', 'chatInput', 'isThinking', 'isStreaming', 'streamingMessageIndex',
        ]),
        viewToggleItems() {
            return [
                { name: 'Charts', icon: 'far fa-chart-bar', dataTest: 'students3d-chart-btn' },
                { name: 'Data Wall', icon: 'far fa-table', dataTest: 'students3d-data-grid-btn' }
            ]
        },
        viewMode: {
            get() {
                return this.showGrid ? this.viewToggleItems[1] : this.viewToggleItems[0]
            },
            set(item) {
                this.showGrid = item && item.name === 'Data Wall'
            }
        },
        records() {
            return this.charts && !this.$_.isEmpty(this.charts.records)
        },
        headline() {
            return this.$_.isEmpty(this.params.search)
                ? 'Start Searching for Students'
                : '0 Students'
        },
        subheading() {
            return this.$_.isEmpty(this.params.search)
                ? 'To get started, begin by choosing a school year and optionally school and grade.'
                : 'Try changing your filter options to broaden the search results.'
        },
    },

    methods: {
        pushChatMessage(entry) {
            // Guard against NaN - ensure sequence is always a valid number
            if (typeof this._chatMessageSeq !== 'number' || isNaN(this._chatMessageSeq)) {
                this._chatMessageSeq = 0
            }
            this._chatMessageSeq += 1
            this.chatMessages.push({
                _stableId: `students3d-chat-msg-${this._chatMessageSeq}`,
                ...entry,
            })
            return this.chatMessages.length - 1
        },

        async sendMessageFromSidebar(content) {
            if (!content) return

            this.pushChatMessage({
                role: 'user',
                content: content,
            })

            this.isThinking = true

            try {
                await this.callLLMStreaming(this.chatMessages)
            } catch (error) {
                console.error('Chat error:', error)
                const friendlyMessage = error?.userFacing
                    ? error.message
                    : 'Sorry, something went wrong. Please try again.'
                this.pushChatMessage({
                    role: 'assistant',
                    msgType: 'text',
                    content: friendlyMessage,
                })
            } finally {
                this.isThinking = false
                this.isStreaming = false
                this.streamingMessageIndex = null
            }
        },

        getWebSocketUrl() {
            const explicitUrl = process.env.VUE_APP_AI_WEBSOCKET_URL
            if (explicitUrl) return explicitUrl

            const baseURL = this.$axios.defaults.baseURL || process.env.VUE_APP_BASE_URI || window.location.origin
            const normalizedBase = baseURL.replace(/\/+$/, '')
            const wsBase = normalizedBase
                .replace(/^https:\/\//i, 'wss://')
                .replace(/^http:\/\//i, 'wss://')
            return `${wsBase}/ec-search-agent`
        },

        initializeWebSocket() {
            const websocketUrl = this.getWebSocketUrl()
            this.closeWebSocket()

            this.wsConnectingPromise = new Promise((resolve, reject) => {
                let settled = false
                let socket

                const timeout = setTimeout(() => {
                    if (!settled) {
                        settled = true
                        this.closeWebSocket()
                        reject(new Error('WebSocket connection timed out'))
                    }
                }, 5000)

                try {
                    socket = new WebSocket(websocketUrl)
                    this.websocket = socket
                } catch (error) {
                    settled = true
                    clearTimeout(timeout)
                    reject(error)
                    return
                }

                // Guard every handler against a superseded socket (e.g. the old one closing
                // after resetChat() has already opened a new one) so a stale close/error event
                // can't clobber wsConnected/wsResolve for the socket that's actually in use now.
                socket.onopen = () => {
                    if (this.websocket !== socket) return
                    if (!settled) {
                        settled = true
                        clearTimeout(timeout)
                        this.wsConnected = true
                        resolve()
                    }
                }

                socket.onmessage = (event) => {
                    if (this.websocket !== socket) return
                    this.handleWebSocketMessage(event.data)
                }

                socket.onerror = () => {
                    if (this.websocket !== socket) return
                    this.wsConnected = false
                }

                socket.onclose = () => {
                    if (this.websocket !== socket) return
                    this.wsConnected = false
                    if (!settled) {
                        settled = true
                        clearTimeout(timeout)
                        reject(new Error('WebSocket connection failed'))
                    }
                    if (this.wsResolve) {
                        this.wsResolve()
                        this.wsResolve = null
                    }
                }
            }).finally(() => {
                this.wsConnectingPromise = null
            })

            return this.wsConnectingPromise
        },

        closeWebSocket() {
            if (this.websocket) {
                const socket = this.websocket
                this.websocket = null
                this.wsConnected = false
                socket.close()
            }
        },

        handleWebSocketMessage(data) {
            try {
                const parsed = JSON.parse(data)

                if (parsed.type === 'connected') return

                if (parsed.type === 'quota_exceeded') {
                    const resetTime = parsed.quota_details?.resetTime
                    let resetText = 'at midnight'
                    if (resetTime) {
                        try {
                            resetText = 'at ' + new Date(resetTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                        } catch (e) { /* use default */ }
                    }
                    this.pushChatMessage({
                        role: 'assistant',
                        msgType: 'final',
                        content: `You've reached your daily usage limit. Your limit resets ${resetText}. Please try again later.`,
                    })
                    if (this.wsResolve) {
                        this.wsResolve()
                        this.wsResolve = null
                    }
                    return
                }

                if (parsed.type === 'complete' || parsed.type === 'error') {
                    if (parsed.type === 'error') {
                        this.pushChatMessage({
                            role: 'assistant',
                            msgType: 'text',
                            content: parsed.message || parsed.error || 'An error occurred',
                        })
                    }
                    if (this.wsResolve) {
                        this.wsResolve()
                        this.wsResolve = null
                    }
                    return
                }

                this.handleStreamChunk(parsed)
            } catch (e) {
                console.warn('Failed to parse WebSocket message:', data)
            }
        },

        async callLLMStreaming(messages) {
            this.streamingMessageIndex = null

            try {
                if (!this.wsConnected || !this.websocket) {
                    await (this.wsConnectingPromise || this.initializeWebSocket())
                }

                if (!this.wsConnected) {
                    throw new Error('WebSocket not connected')
                }

                // Primary: what year is in the search bar (user's working context)
                const schoolYearId = Number(
                    this.params?.search?.school_year_id
                    || this.sessionUser?.currentYear?.id
                )
                if (!Number.isFinite(schoolYearId) || schoolYearId <= 0) {
                    const err = new Error('Please select a school year before using AI search.')
                    err.userFacing = true
                    throw err
                }

                const searchBar = this.$parent?.$refs?.demographicSearch
                const schoolYearName = searchBar?.schoolYear?.name
                    || this.sessionUser?.currentYear?.name

                // Also send actual current year for context (so agent knows what "this year" means)
                const currentSchoolYearId = this.sessionUser?.currentYear?.id
                    ? Number(this.sessionUser.currentYear.id)
                    : null
                const currentSchoolYearName = this.sessionUser?.currentYear?.name || null

                // The history already ends with the latest user message
                const queryWithContext = messages
                    .filter(msg => {
                        if (msg.role === 'user') return true
                        if (msg.role === 'assistant' && msg.msgType === 'final' && msg.content) return true
                        return false
                    })
                    .map(msg => {
                        const role = msg.role === 'user' ? 'User' : 'Assistant'
                        return `${role}: ${msg.content.trim()}`
                    })
                    .join('\n\n')

                this.isStreaming = true

                const responsePromise = new Promise(resolve => {
                    this.wsResolve = resolve
                })

                // Option buttons change these without the agent, so it has to be told
                const toAppliedItems = list => (list || []).map(({ id, name }) => ({ id, name }))

                const searchData = {
                    query: queryWithContext,
                    school_year_id: schoolYearId,
                    school_year_name: schoolYearName,
                    current_school_year_id: currentSchoolYearId,
                    current_school_year_name: currentSchoolYearName,
                    applied_schools: toAppliedItems(searchBar?.schools),
                    applied_grades: toAppliedItems(searchBar?.grades),
                }

                this.websocket.send(JSON.stringify({
                    event: 'search',
                    data: searchData,
                }))

                await responsePromise

            } catch (error) {
                console.error('LLM API error:', error)
                throw error
            }
        },

        async handleStreamChunk(parsed) {
            if (this.isThinking) {
                this.isThinking = false
            }

            const currentMsg = () => this.streamingMessageIndex !== null
                ? this.chatMessages[this.streamingMessageIndex]
                : null

            const pushBubble = (props) => {
                const idx = this.pushChatMessage({ role: 'assistant', ...props })
                this.streamingMessageIndex = idx
            }

            switch (parsed.type) {
                case 'status':
                case 'thinking': {
                    let msg = currentMsg()
                    if (!msg || msg.msgType !== 'thinking') {
                        pushBubble({ msgType: 'thinking', thinking: '' })
                        msg = currentMsg()
                    }
                    msg.thinking = (msg.thinking || '') + (parsed.content || parsed.message || '')
                    break
                }
                case 'text': {
                    let msg = currentMsg()
                    if (!msg || msg.msgType !== 'text') {
                        pushBubble({ msgType: 'text', content: '', inCodeBlock: false, codeBlockBuffer: '' })
                        msg = currentMsg()
                    }
                    const textContent = parsed.content || ''
                    if (textContent.includes('```')) {
                        const parts = textContent.split('```')
                        for (let i = 0; i < parts.length; i++) {
                            if (i > 0) msg.inCodeBlock = !msg.inCodeBlock
                            if (msg.inCodeBlock) {
                                msg.codeBlockBuffer += parts[i]
                            } else {
                                msg.content += parts[i]
                            }
                        }
                    } else if (msg.inCodeBlock) {
                        msg.codeBlockBuffer += textContent
                    } else {
                        msg.content += textContent
                    }
                    break
                }
                case 'tool_call': {
                    let msg = currentMsg()
                    if (!msg || msg.msgType !== 'tool') {
                        pushBubble({ msgType: 'tool', tools: [] })
                        msg = currentMsg()
                    }
                    msg.tools.push({ name: parsed.name, status: 'calling' })
                    break
                }
                case 'tool_result': {
                    const msg = currentMsg()
                    if (msg && msg.msgType === 'tool' && msg.tools.length) {
                        const idx = msg.tools.length - 1
                        this.$set(msg.tools, idx, { ...msg.tools[idx], status: 'done' })
                    }
                    break
                }
                case 'final': {
                    if (parsed.result) {
                        const responseType = parsed.result.type
                        const queryId = parsed.queryId

                        // Remove any streamed narrative bubble before showing the real result below —
                        // the model can narrate success before we've actually verified it.
                        const streamingMsg = currentMsg()
                        if (streamingMsg && streamingMsg.msgType === 'text' &&
                            this.streamingMessageIndex !== null &&
                            this.streamingMessageIndex < this.chatMessages.length &&
                            this.chatMessages[this.streamingMessageIndex] === streamingMsg) {
                            this.chatMessages.splice(this.streamingMessageIndex, 1)
                            this.streamingMessageIndex = null
                        }

                        if (!responseType) {
                            console.error('Agent response missing required "type" field:', JSON.stringify(parsed.result, null, 2))
                            pushBubble({
                                msgType: 'final',
                                content: 'Sorry, I received an invalid response. Please try again.',
                                queryId,
                            })
                            break
                        }

                        switch (responseType) {
                            case 'quota_exceeded': {
                                pushBubble({
                                    msgType: 'final',
                                    content: parsed.result.message || "You've reached your daily usage limit. Please try again later.",
                                    queryId,
                                })
                                break
                            }

                            case 'token_limit_exceeded': {
                                pushBubble({
                                    msgType: 'final',
                                    content: parsed.result.message || "I'm sorry, this conversation has exceeded the token limit. Please click the refresh button above the chat text field or refresh the page to start a new session.",
                                    queryId,
                                })
                                break
                            }

                            case 'remove_filters': {
                                if (parsed.result.remove_filters && Array.isArray(parsed.result.remove_filters)) {
                                    pushBubble({ msgType: 'final', content: '', queryId })
                                    this.removeFilters(parsed.result.remove_filters, currentMsg())
                                }
                                break
                            }

                            case 'partial': {
                                pushBubble({ msgType: 'final', content: parsed.result.message || 'Please choose an option:', queryId })
                                const msg = currentMsg()
                                this.$set(msg, 'options', parsed.result.options)
                                // Apply any non-ambiguous filters/search that are already resolved
                                await this.applyAgentResult(parsed.result, msg)
                                // Restore the message and options after applyAgentResult
                                msg.content = parsed.result.message || 'Please choose an option:'
                                this.$set(msg, 'options', parsed.result.options)
                                break
                            }

                            case 'ambiguous': {
                                pushBubble({ msgType: 'final', content: parsed.result.message || 'Please choose an option:', queryId })
                                this.$set(currentMsg(), 'options', parsed.result.options)
                                break
                            }

                            case 'success': {
                                pushBubble({ msgType: 'final', content: '', queryId })
                                this.applyAgentResult(parsed.result, currentMsg())
                                break
                            }

                            case 'conversational': {
                                if (parsed.result.message) {
                                    pushBubble({ msgType: 'final', content: parsed.result.message, queryId })
                                }
                                break
                            }

                            default: {
                                console.error('Unknown response type:', responseType, parsed.result)
                                pushBubble({
                                    msgType: 'final',
                                    content: 'Sorry, I received an unexpected response type. Please try again.',
                                    queryId,
                                })
                                break
                            }
                        }
                    }
                    if (this.wsResolve) {
                        this.wsResolve()
                        this.wsResolve = null
                    }
                    break
                }
            }
        },


        /**
         * Apply a filter based on its type using the appropriate handler.
         * @param {Object} filterEntry - Filter entry with type and params
         * @param {Object} msg - Message object for error reporting
         * @returns {Promise<string|null>} Success message or null
         */
        async applyFilterByType(filterEntry, msg) {
            try {
                const context = {
                    params: this.params,
                    groupers: this.groupers,
                }

                let filterResult = null

                switch (filterEntry.type) {
                    case 'gender': {
                        filterResult = await NlfAgentProcessor.applyGenderFilter(
                            filterEntry,
                            msg,
                            context,
                            this.$parent
                        )
                        break
                    }

                    case 'ethnicity': {
                        filterResult = await NlfAgentProcessor.applyEthnicityFilter(
                            filterEntry,
                            msg,
                            context,
                            this.$parent
                        )
                        break
                    }

                    case 'disability': {
                        filterResult = await NlfAgentProcessor.applyDisabilityFilter(
                            filterEntry,
                            msg,
                            context,
                            this.$parent
                        )
                        break
                    }

                    case 'meal_status': {
                        filterResult = await NlfAgentProcessor.applyMealStatusFilter(
                            filterEntry,
                            msg,
                            context,
                            this.$parent
                        )
                        break
                    }

                    case 'demographic': {
                        filterResult = await NlfAgentProcessor.applyDemographicFilter(
                            filterEntry,
                            msg,
                            context,
                            this.$parent
                        )
                        break
                    }

                    case 'assessment': {
                        filterResult = await NlfAgentProcessor.applyAssessmentFilter(
                            filterEntry,
                            msg,
                            { ...context, axios: this.$axios, modelGet: this.$modelGet },
                            this.$parent
                        )
                        break
                    }

                    case 'attendance': {
                        filterResult = await NlfAgentProcessor.applyAttendanceFilter(
                            filterEntry,
                            msg,
                            context,
                            this.$parent
                        )
                        break
                    }

                    case 'incident': {
                        filterResult = await NlfAgentProcessor.applyIncidentFilter(
                            filterEntry,
                            msg,
                            context,
                            this.$parent
                        )
                        break
                    }

                    case 'tag': {
                        filterResult = await NlfAgentProcessor.applyTagFilter(
                            filterEntry,
                            msg,
                            context,
                            this.$parent
                        )
                        break
                    }

                    case 'course': {
                        filterResult = await NlfAgentProcessor.applyCourseFilter(
                            filterEntry,
                            msg,
                            context,
                            this.$parent
                        )
                        break
                    }

                    default: {
                        console.warn(`Filter type "${filterEntry.type}" not yet implemented`)
                        return null
                    }
                }

                if (filterResult) {
                    if (filterResult.updatedFilters) {
                        this.params.filters = filterResult.updatedFilters
                    }
                    if (filterResult.updatedGroupers) {
                        this.groupers = filterResult.updatedGroupers
                    }
                    // If the filter has a grouper, set it as the active grouping
                    if (filterResult?.filter?.grouper && this.$parent) {
                        this.$parent.activeGrouping = this.$parent.computeGrouping(filterResult.filter.grouper)
                    }
                    return filterResult.message || null
                }

                return null
            } catch (error) {
                console.error(`Error applying ${filterEntry.type} filter:`, error)
                msg.content = `Error applying filter: ${error.message}`
                return null
            }
        },

        async applyAssessmentFilter(result, msg) {
            const context = {
                params: this.params,
                groupers: this.groupers,
                axios: this.$axios,
                modelGet: this.$modelGet,
            }
            const applyResult = await NlfAgentProcessor.applyAssessmentFilter(result, msg, context, this.$parent)
            if (applyResult) {
                this.params.filters = applyResult.updatedFilters
                this.groupers = applyResult.updatedGroupers
                msg.content = applyResult.message
            }
        },


        /**
         * Switch the active group-by tab based on the agent's canonical "group_by" value.
         * Reuses View.vue's onGroupTabClick so agent-driven and manual tab clicks behave identically.
         *
         * The agent can't see security codes or district config, so it may ask for a grouping
         * that's real but unavailable to this user. Only the FE knows why, so this reports the
         * specific reason — permissions, or a config/feature gate.
         *
         * @param {string} groupBy - Canonical grouping name from the agent (e.g. "Gender", "Overall")
         * @returns {{message: string, suppressClosing?: boolean}|null} What to tell the user, or null
         *   if nothing needs to be said. suppressClosing means the grouping wasn't actually applied —
         *   skip the "Need changes?" trailer and feedback icons, since there's nothing to rate.
         */
        applyGroupBy(groupBy) {
            if (!this.$parent) return null

            const tab = this.$parent.groupTabs?.find(t => t.name === groupBy)
            if (tab) {
                this.$parent.onGroupTabClick(tab)
                return { message: groupBy === 'Overall' ? 'Grouping cleared' : `Grouped by: **${groupBy}**` }
            }

            // School Group is hidden only when the district has none configured.
            if (groupBy === 'School Group') {
                console.warn('group_by "School Group" is unavailable: no school groups configured for this district')
                return {
                    message: 'No School Groups are currently configured. Would you like to group by school?',
                    suppressClosing: true,
                }
            }

            // EL Status/ELP Level are hidden by a district feature toggle.
            if ((groupBy === 'EL Status' || groupBy === 'ELP Level') && !this.$parent.ellLevelEnabled) {
                console.warn(`group_by "${groupBy}" is unavailable: English Learner grouping is disabled for this district`)
                return { message: 'English Learner grouping is not currently enabled for this district.', suppressClosing: true }
            }

            // Tell "real grouping, just not available" apart from "not a recognized grouping."
            const isRecognizedGrouping = groupBy === 'Overall' || (this.$parent.groupers || []).some(g =>
                g.name === groupBy || (g.subGroupers || []).some(sub => sub.name === groupBy)
            )

            if (isRecognizedGrouping) {
                console.warn(`group_by "${groupBy}" is a recognized grouping but not available to this user`)
                return { message: `You do not have the required permissions to access student ${groupBy} data. If you believe you should have access, please reach out to your administrator.`, suppressClosing: true }
            }

            console.warn(`group_by "${groupBy}" does not match a recognized grouping`)
            return { message: `Sorry, "${groupBy}" isn't a grouping I recognize. Please use the group-by tabs above to group manually.`, suppressClosing: true }
        },

        async applyAgentResult(result, msg) {
            // The agent can clear filters in the same answer that applies new ones ("clear that, I
            // want to see X"). Same list format as a remove_filters response.
            // A success can carry an explanation or follow-up question; without this it would be dropped.
            // Partial answers show their own message, so they are left alone.
            const agentNote = result.type === 'success' && typeof result.message === 'string' ? result.message.trim() : ''
            const removals = Array.isArray(result.remove_filters) ? result.remove_filters : []
            const hasRemovals = removals.length > 0
            // What the agent sent last no longer matches the screen once something is cleared.
            // Keeping it would make an identical re-send (e.g. "add it back") look unchanged and
            // get skipped.
            if (hasRemovals) {
                this._lastAgentFilters = []
            }

            // Skip straight to group_by, with no backend call, if nothing else changed since last time.
            const searchUnchanged = this.$_.isEqual(result.search || null, this._lastAgentSearch || null)
            const filtersUnchanged = this.$_.isEqual(result.filters || [], this._lastAgentFilters || [])
            if (searchUnchanged && filtersUnchanged && !hasRemovals) {
                if (result.group_by) {
                    const groupByOutcome = this.applyGroupBy(result.group_by)
                    if (groupByOutcome) {
                        msg.content = groupByOutcome.suppressClosing
                            ? groupByOutcome.message
                            : `${groupByOutcome.message}\n\nNeed changes? Just ask, or use the filter buttons above.`
                        // It's a clarifying question, not a completed action — hide the feedback icons.
                        if (groupByOutcome.suppressClosing) {
                            msg.queryId = null
                        }
                    }
                }
                if (agentNote) {
                    msg.content = msg.content ? `${msg.content}\n\n${agentNote}` : agentNote
                }
                return
            }
            // Clone, since the loop below mutates filterEntry.params in place.
            this._lastAgentSearch = this.$_.cloneDeep(result.search) || null
            this._lastAgentFilters = this.$_.cloneDeep(result.filters) || []

            // Batch all updates to prevent multiple executeSearch calls
            // Set executeSearchInProgress flag on parent (View.vue) to suppress search watcher
            // Checked independently so a turn that only changes one of these doesn't re-show a
            // confirmation for the other — e.g. switching grouping shouldn't restate "Gender: Female."
            const hasSearchUpdates = !searchUnchanged && !!result.search
            const hasFilterUpdates = !filtersUnchanged && result.filters && result.filters.length > 0

            // Suppress watcher if we're applying any combination of updates
            if ((hasSearchUpdates || hasFilterUpdates || hasRemovals) && this.$parent) {
                this.$parent.executeSearchInProgress = true
            }

            const appliedMessages = []
            let suppressClosing = false

            try {
                // Clear first, so a filter that is both removed and re-sent ends up applied.
                if (hasRemovals) {
                    const removedLabels = this.removeFiltersFromResult(removals)
                    if (removedLabels.length > 0) {
                        appliedMessages.push(`Removed: ${removedLabels.map(label => `**${label}**`).join(', ')}`)
                    }
                }

                // Apply search params (demographic search bar) if present
                if (hasSearchUpdates) {
                    const searchContext = {
                        demographicSearchRef: this.$parent?.$refs?.demographicSearch,
                    }
                    const searchResult = NlfAgentProcessor.applySearchParams(result.search, searchContext)
                    if (searchResult) {
                        if (searchResult.updates) {
                            this.params.search = { ...this.params.search, ...searchResult.updates }
                        }
                        if (searchResult.message) {
                            appliedMessages.push(searchResult.message)
                        }
                    }
                }

                // Empty when filters didn't actually change, to skip the clear/re-apply loop below.
                const filters = hasFilterUpdates ? (result.filters || []) : []

                // Clear existing filters when agent sends new filters to apply
                const filterTypesToClear = ['assessment', 'ethnicity', 'gender']
                filterTypesToClear.forEach(filterType => {
                    const hasNewFilters = filters.some(f => f.type === filterType)
                    if (hasNewFilters) {
                        this.clearFiltersByType(filterType)
                    }
                })

                for (const filterEntry of filters) {
                    // Any missing expected filter-level school_year_ids will be autofilled with
                    // the search's school_year_id in executeSearch(), once that's available and
                    // just before the students3D API call is sent
                    const filterMessage = await this.applyFilterByType(filterEntry, msg)
                    if (filterMessage) {
                        appliedMessages.push(filterMessage)
                    }
                }

                // Apply group_by (switch the active grouping tab) if present
                if (result.group_by) {
                    const groupByOutcome = this.applyGroupBy(result.group_by)
                    if (groupByOutcome) {
                        appliedMessages.push(groupByOutcome.message)
                        if (groupByOutcome.suppressClosing) {
                            suppressClosing = true
                            // Clarifying question, not a completed action — hide the feedback icons.
                            msg.queryId = null
                        }
                    }
                }

                if (agentNote) {
                    if (appliedMessages.length > 0) appliedMessages.push(`\n${agentNote}`)
                    else msg.content = agentNote
                }

                // Set combined message after all updates
                if (appliedMessages.length > 0) {
                    msg.content = suppressClosing
                        ? appliedMessages.join('\n')
                        : `${appliedMessages.join('\n')}\n\nNeed changes? Just ask, or use the filter buttons above.`
                }
            } catch (error) {
                console.error('Error applying agent result:', error)
                msg.content = `Error applying changes: ${error.message}`
            } finally {
                // Release the flag and manually trigger search since watchers were suppressed
                if ((hasSearchUpdates || hasFilterUpdates || hasRemovals) && this.$parent) {
                    this.$parent.executeSearchInProgress = false
                    this.$parent.executeSearch()
                }
            }
        },

        /**
         * Apply a remove_filters list that arrived alongside a normal result. Same list format as a
         * remove_filters response, but returns what was removed (for the combined chat message)
         * instead of writing a message of its own.
         * @param {Array<string|Object>} removals - filter types, 'all', or { type, params } objects
         * @returns {string[]} chip labels of the filters that were removed
         */
        removeFiltersFromResult(removals) {
            const before = this.params.filters.slice()
            if (removals.includes('all')) {
                this.resetFiltersAndGrouping()
            } else {
                const removedItems = []
                removals.forEach(item => {
                    if (typeof item === 'string') {
                        this.removeFiltersByType(item, removedItems)
                    } else if (item && typeof item === 'object' && item.type) {
                        this.removeSpecificFilter(item, removedItems)
                    }
                })
            }
            return before.filter(f => !this.params.filters.includes(f)).map(f => f.chipText || f.type)
        },

        removeFilters(filterTypes, msg) {
            // Same reason as in applyAgentResult: the remembered agent filters no longer match the
            // screen, and "add it back" would otherwise be skipped as a repeat.
            this._lastAgentFilters = []

            if (filterTypes.includes('all')) {
                this.removeAllFilters(msg)
                return
            }

            const removedItems = []

            filterTypes.forEach(item => {
                if (typeof item === 'string') {
                    this.removeFiltersByType(item, removedItems)
                } else if (typeof item === 'object' && item !== null && item.type) {
                    this.removeSpecificFilter(item, removedItems)
                }
            })

            this.setRemovalMessage(msg, removedItems)
        },

        removeAllFilters(msg) {
            const hadFilters = this.params.filters.length > 0
            this.resetFiltersAndGrouping()
            if (hadFilters) {
                msg.content = 'All filters cleared.\n\nPlease let me know if you\'d like me to apply new search criteria.'
            }
        },

        resetFiltersAndGrouping() {
            this.params.filters = []
            this.groupers = this.groupers.filter(g => !g.type || g.type !== 'controller')
            if (this.$parent?.activeGrouping?.grouper) {
                this.$parent.activeGrouping = Group.none(this.charts)
            }
        },

        removeFiltersByType(filterType, removedItems) {
            // Applied demographic filters are typed `demo_<id>` (one per demographic), so the
            // agent's "demographic" removes all of them.
            const matchesType = f => filterType === 'demographic'
                ? isDemographicFilterType(f.type)
                : f.type === filterType
            const filtersToRemove = this.params.filters.filter(matchesType)
            if (filtersToRemove.length > 0) {
                this.params.filters = this.params.filters.filter(f => !matchesType(f))
                filtersToRemove.forEach(filter => {
                    if (filter.grouper) {
                        this.groupers = this.groupers.filter(g => g.id !== filter.grouper.id)
                        // If this grouper was driving the active chart tab, fall back to the
                        // default "Students" grouping instead of leaving stale per-value buckets
                        if (this.$parent?.isActiveGrouper(filter.grouper)) {
                            this.$parent.activeGrouping = Group.none(this.charts)
                        }
                    }
                })
                removedItems.push(filterType)
            }
        },

        removeSpecificFilter(filterDescriptor, removedItems) {
            const filterToRemove = this.findMatchingFilter(filterDescriptor)
            if (filterToRemove) {
                this.params.filters = this.params.filters.filter(f => f !== filterToRemove)
                if (filterToRemove.grouper) {
                    this.groupers = this.groupers.filter(g => g.id !== filterToRemove.grouper.id)
                    if (this.$parent?.isActiveGrouper(filterToRemove.grouper)) {
                        this.$parent.activeGrouping = Group.none(this.charts)
                    }
                }
                removedItems.push(`specific ${filterDescriptor.type}`)
            }
        },

        setRemovalMessage(msg, removedItems) {
            if (removedItems.length > 0) {
                if (!msg.content) {
                    const filterNames = removedItems.map(t => typeof t === 'string' ? t.charAt(0).toUpperCase() + t.slice(1) : t).join(', ')
                    msg.content = `${filterNames} filter${removedItems.length > 1 ? 's' : ''} removed.`
                }
                msg.content += '\n\nPlease let me know if you\'d like me to apply new search criteria.'
            } else {
                msg.content = 'No matching filters found to remove.'
            }
        },

        /**
         * Clear existing filters of a specific type and their associated groupers.
         * @param {string} filterType - The type of filter to clear (e.g., 'assessment', 'ethnicity', 'gender')
         */
        clearFiltersByType(filterType) {
            const existingFilters = this.params.filters.filter(f => f.type === filterType)
            if (existingFilters.length > 0) {
                this.params.filters = this.params.filters.filter(f => f.type !== filterType)
                this.groupers = this.groupers.filter(g => {
                    return !existingFilters.some(f => f.grouper && f.grouper.id === g.id)
                })
            }
        },

        /**
         * Find a filter that matches the specified filter descriptor.
         * @param {Object} filterDescriptor - Object with type and params to match
         * @returns {Object|null} Matching filter or null
         */
        findMatchingFilter(filterDescriptor) {
            const { type, params } = filterDescriptor

            // Applied demographic filters are typed `demo_<id>` and there is only ever one per
            // demographic, so the id alone identifies it.
            if (type === 'demographic') {
                return this.params.filters.find(f => f.type === `demo_${params?.demographic_id}`)
            }

            // There is only ever one course filter, so the teacher identifies it.
            if (type === 'course') {
                return this.params.filters.find(f => f.type === 'course' && Number(f.params?.user_id) === Number(params?.user_id))
            }

            return this.params.filters.find(f => {
                if (f.type !== type) return false

                // For assessment filters, match key identifying fields
                if (type === 'assessment') {
                    // Extract IDs and names from incoming params object format
                    const incomingGroupId = params.data_point_type?.id
                    const incomingGroupName = params.data_point_type?.name
                    const incomingAssessmentId = params.sub_category?.id
                    const incomingAssessmentName = params.sub_category?.display_name || params.sub_category?.name
                    const incomingWindowId = params.data_point_name?.id
                    const incomingWindowName = params.data_point_name?.name

                    const matchesGroup = this.assessmentFieldMatches(
                        f.params.data_point_type_id,
                        incomingGroupId,
                        f.editorParams?.data_point_type?.name,
                        incomingGroupName
                    )

                    const matchesAssessment = this.assessmentFieldMatches(
                        f.params.sub_category_id,
                        incomingAssessmentId,
                        f.editorParams?.sub_category?.name || f.editorParams?.sub_category?.display_name,
                        incomingAssessmentName
                    )

                    const matchesWindow = this.assessmentFieldMatches(
                        f.params.data_point_name_id,
                        incomingWindowId,
                        f.editorParams?.data_point_name?.name,
                        incomingWindowName
                    )

                    const matchesMinScore = !params.minimum_score || f.params.minimum_score === params.minimum_score
                    const matchesMaxScore = !params.maximum_score || f.params.maximum_score === params.maximum_score

                    const matchesScoreDetailType = !params.score_detail_type || this.assessmentFieldMatches(
                        f.params.score_detail_type_id,
                        params.score_detail_type?.id,
                        f.editorParams?.score_detail_type?.code || f.editorParams?.score_detail_type?.description,
                        params.score_detail_type?.code || params.score_detail_type?.description
                    )

                    const matchesTargetSet = !params.target_set || this.assessmentFieldMatches(
                        f.params.target_set_id,
                        params.target_set?.id,
                        f.editorParams?.target_set?.name,
                        params.target_set?.name
                    )

                    const matchesTargetDescriptor = !params.target_descriptor?.length || this.arraysEqual(
                        f.params.target_descriptor_id || [],
                        params.target_descriptor.map(d => d.id)
                    )

                    return matchesGroup && matchesAssessment && matchesWindow && matchesMinScore && matchesMaxScore
                        && matchesScoreDetailType && matchesTargetSet && matchesTargetDescriptor
                }

                // For gender filters, match gender array values
                if (type === 'gender') {
                    return this.arraysEqual(f.params.gender, params.gender)
                }

                // For ethnicity filters, match ethnicity array values
                if (type === 'ethnicity') {
                    return this.arraysEqual(f.params.ethnicity, params.ethnicity)
                }

                // Default: no match
                return false
            })
        },

        /**
         * Check if an assessment filter field matches by ID or name.
         * @param {number} filterId - The ID stored in the filter params
         * @param {number} paramsId - The ID to match against (if provided)
         * @param {string} filterName - The name stored in the filter editorParams
         * @param {string} paramsName - The name to match against (if provided)
         * @returns {boolean} True if matches by ID or name
         */
        assessmentFieldMatches(filterId, paramsId, filterName, paramsName) {
            // Match by ID or name — Agent sometimes sends placeholder id which won't match
            // validated id even though the name is correct.
            if (filterId != null && paramsId != null && filterId === paramsId) return true
            if (filterName && paramsName && filterName === paramsName) return true
            return false
        },

        /**
         * Check if two arrays contain the same elements (order-independent).
         * Uses JSON.stringify on sorted arrays for consistency with codebase comparison patterns.
         * @param {Array} arr1 - First array
         * @param {Array} arr2 - Second array
         * @returns {boolean} True if arrays contain same elements
         */
        arraysEqual(arr1, arr2) {
            if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false
            if (arr1.length !== arr2.length) return false
            return JSON.stringify([...arr1].sort()) === JSON.stringify([...arr2].sort())
        },

        /**
         * Build a demographic filter option from an array of items.
         * @param {Array} items - Array of demographic items (from editorParams)
         * @param {string} filterType - Type of filter ('gender', 'ethnicity', 'disability')
         * @param {Object} config - Configuration for the specific filter type
         * @param {Object} existingFilter - Existing filter to preserve additional params from
         * @returns {Object} Filter option object
         */
        buildDemographicOption(items, filterType, config, existingFilter) {
            const itemNameKey = config.itemNameKey || 'display_name'
            const displayNames = items.map(item => item[itemNameKey])
            const displayNameGroups = items.map(item => item.display_name_group)
            const hasAnyGroups = displayNameGroups.some(g => g)

            // Use collectIdsFromSelection to properly expand display_name_group arrays
            const expandedValues = FilterPanelUtil.collectIdsFromSelection(items, {
                idKey: config.valueKey,
                parseInteger: true
            })

            const params = {
                [config.paramKey]: expandedValues,
                display_name: displayNames
            }

            if (hasAnyGroups) {
                params.display_name_group = displayNameGroups
            }

            // Preserve additional params for filters that need them (like disability)
            if (config.preserveParams && existingFilter) {
                config.preserveParams.forEach(key => {
                    if (existingFilter.params?.[key] !== undefined) {
                        params[key] = existingFilter.params[key]
                    }
                })
            }

            const option = {
                type: filterType,
                params
            }

            // Preserve restrict if present
            if (existingFilter?.restrict !== undefined) {
                option.restrict = existingFilter.restrict
            }

            return option
        },

        /**
         * Remove a specific value from a filter (gender/ethnicity/disability).
         * If the filter only has one value, removes the entire filter.
         * @param {Object} option - The option to remove
         * @param {string} filterType - Type of filter ('gender', 'ethnicity', 'disability')
         * @param {Object} config - Configuration for the specific filter type
         * @returns {void}
         */
        removeFilterOption(option, filterType, config) {
            const existingFilter = this.params.filters.find(f => f.type === filterType)
            if (!existingFilter) return

            // Special handling for incident (remove from specific multiselect field)
            if (filterType === 'incident' && config.multiFields) {
                this.removeIncidentFilterOption(option, existingFilter, config)
                return
            }

            const valuesToRemove = option.params[config.paramKey]

            const remainingItems = existingFilter.editorParams?.[config.editorKey]?.filter(
                item => !valuesToRemove.includes(item[config.valueKey])
            )

            // Remove entire filter when clearing last option of a filter type
            if (!remainingItems || remainingItems.length === 0) {
                this.params.filters = this.params.filters.filter(f => f.type !== filterType)
                if (existingFilter.grouper) {
                    this.groupers = this.groupers.filter(g => g.id !== existingFilter.grouper.id)
                }
                if (this.$parent) {
                    this.$parent.executeSearch()
                }
                return
            }

            // Rebuild filter with remaining items
            const rebuiltOption = this.buildDemographicOption(remainingItems, filterType, config, existingFilter)

            // Don't clear filters here - let applyFilterByType handle replacement atomically
            // to avoid triggering watchers with an empty filter array
            const msg = { content: '' }
            this.applyFilterByType(rebuiltOption, msg).then(() => {
                if (this.$parent) {
                    this.$parent.executeSearch()
                }
            }).catch(error => {
                console.error(`Error updating ${filterType} filter:`, error)
            })
        },

        removeIncidentFilterOption(option, existingFilter, config) {
            const rebuiltParams = {}
            const rebuiltEditorObjects = {}

            // If option doesn't have _editorObjects, build them from params (agent-provided option)
            if (!option._editorObjects) {
                option._editorObjects = this.buildEditorObjectsFromParams(option, config)
            }

            // Remove values from the specific field they belong to
            config.multiFields.forEach(field => {
                const valuesToRemove = option.params?.[field.paramKey] || []
                const existingIds = existingFilter.params?.[field.paramKey] || []
                const existingObjects = existingFilter.editorParams?.[field.editorKey] || []

                // Calculate remaining values (filter out if removing, otherwise keep all)
                const remainingIds = valuesToRemove.length > 0
                    ? existingIds.filter(id => !valuesToRemove.includes(id))
                    : existingIds

                const remainingObjects = valuesToRemove.length > 0
                    ? existingObjects.filter(obj => !valuesToRemove.includes(obj.id))
                    : existingObjects

                // Only add non-empty fields to rebuilt filter
                if (remainingIds.length > 0) {
                    rebuiltParams[field.paramKey] = remainingIds
                    rebuiltEditorObjects[field.editorKey] = remainingObjects
                }
            })

            // Preserve non-multiselect params
            if (config.preserveParams) {
                config.preserveParams.forEach(key => {
                    if (existingFilter.params?.[key] !== undefined) {
                        rebuiltParams[key] = existingFilter.params[key]
                    }
                    if (existingFilter.editorParams?.[key] !== undefined) {
                        rebuiltEditorObjects[key] = existingFilter.editorParams[key]
                    }
                })
            }

            // Check if all multiselect fields are now empty
            const hasAnyValues = config.multiFields.some(field =>
                rebuiltParams[field.paramKey] && rebuiltParams[field.paramKey].length > 0
            )

            if (!hasAnyValues) {
                // Remove entire filter
                this.params.filters = this.params.filters.filter(f => f.type !== 'incident')
                if (existingFilter.grouper) {
                    this.groupers = this.groupers.filter(g => g.id !== existingFilter.grouper.id)
                }
                if (this.$parent) {
                    this.$parent.executeSearch()
                }
                return
            }

            // Rebuild filter with remaining values
            const rebuiltOption = {
                type: 'incident',
                params: rebuiltParams,
                restrict: existingFilter.restrict,
                // Pass editor objects for proper reconstruction
                _editorObjects: rebuiltEditorObjects
            }

            const msg = { content: '' }
            // Await the filter update before executing search to prevent race condition
            this.applyFilterByType(rebuiltOption, msg).then(() => {
                if (this.$parent) {
                    this.$parent.executeSearch()
                }
            }).catch(error => {
                console.error('Error updating incident filter:', error)
                // Still execute search even on error to maintain UI consistency
                if (this.$parent) {
                    this.$parent.executeSearch()
                }
            })
        },

        /**
         * Check if an option is already selected in an existing filter.
         * @param {Object} option - The option to check
         * @param {string} filterType - Type of filter ('gender', 'ethnicity', 'disability')
         * @param {Object} config - Configuration for the specific filter type
         * @returns {boolean} True if the option is already selected
         */
        isFilterOptionSelected(option, filterType, config) {
            const existingFilter = this.params.filters.find(f => f.type === filterType)
            if (!existingFilter) return false

            // Special handling for incident (check all multiselect fields)
            if (filterType === 'incident' && config.multiFields) {
                // Check if any of the incident's multiselect fields have values in this option
                for (const field of config.multiFields) {
                    const optionValues = option.params[field.paramKey] || []
                    const currentValues = existingFilter.params?.[field.paramKey] || []

                    if (optionValues.length > 0) {
                        // For this field, ALL values must be in the filter
                        return optionValues.every(val => currentValues.includes(val))
                    }
                }
                return false
            }

            const optionValues = option.params[config.paramKey] || []
            const currentValues = existingFilter.params?.[config.paramKey] || []

            // For combined options (multiple values), ALL values must be in the filter
            return optionValues.every(val => currentValues.includes(val))
        },

        /**
         * Build editor objects from agent-provided params (converts IDs+names to objects)
         */
        buildEditorObjectsFromParams(option, config) {
            const editorObjects = {}

            config.multiFields.forEach(field => {
                const ids = option.params?.[field.paramKey] || []
                const names = option.params?.[field.nameKey] || []

                if (ids.length > 0) {
                    editorObjects[field.editorKey] = ids.map((id, idx) => ({
                        id,
                        name: names[idx] || String(id)
                    }))
                }
            })

            // Preserve scalar params
            if (config.preserveParams) {
                config.preserveParams.forEach(key => {
                    if (option.params?.[key] !== undefined) {
                        editorObjects[key] = option.params[key]
                    }
                })
            }

            return editorObjects
        },

        /**
         * Merge incident filter option with existing incident filter.
         * Incident filters have 4 separate multiselect fields that need independent merging.
         */
        mergeIncidentFilterOption(option, existingFilter, config) {
            const mergedParams = {}
            const mergedEditorObjects = {}

            // If option doesn't have _editorObjects, build them from params (agent-provided option)
            const newEditorObjects = option._editorObjects || this.buildEditorObjectsFromParams(option, config)

            // Merge each multiselect field independently
            config.multiFields.forEach(field => {
                const newIds = option.params?.[field.paramKey] || []
                const newObjects = newEditorObjects[field.editorKey] || []
                const existingIds = existingFilter.params?.[field.paramKey] || []
                const existingObjects = existingFilter.editorParams?.[field.editorKey] || []

                if (newIds.length > 0) {
                    mergedParams[field.paramKey] = [...existingIds, ...newIds]
                    mergedEditorObjects[field.editorKey] = [...existingObjects, ...newObjects]
                } else if (existingIds.length > 0) {
                    mergedParams[field.paramKey] = existingIds
                    mergedEditorObjects[field.editorKey] = existingObjects
                }
            })

            // Preserve non-multiselect params
            if (config.preserveParams) {
                config.preserveParams.forEach(key => {
                    if (existingFilter.params?.[key] !== undefined) {
                        mergedParams[key] = existingFilter.params[key]
                    }
                    if (existingFilter.editorParams?.[key] !== undefined) {
                        mergedEditorObjects[key] = existingFilter.editorParams[key]
                    }
                })
            }

            const mergedOption = {
                type: 'incident',
                params: mergedParams,
                restrict: option.restrict !== undefined ? option.restrict : existingFilter.restrict,
                id: existingFilter.id,  // Reuse existing filter ID so grouper property path stays valid
                _editorObjects: mergedEditorObjects
            }

            // Apply the merged filter
            if (this.$parent) {
                this.$parent.executeSearchInProgress = true
            }
            try {
                this.pushChatMessage({ role: 'assistant', msgType: 'final', content: '' })
                const msg = this.chatMessages[this.chatMessages.length - 1]
                this.applyFilterByType(mergedOption, msg).then(filterMessage => {
                    if (filterMessage) {
                        const closingMessage = 'Need changes? Just ask, or use the filter buttons above.'
                        msg.content = `${filterMessage}\n\n${closingMessage}`
                    }
                }).catch(error => {
                    console.error('Error applying merged incident filter:', error)
                    msg.content = `Error applying filter: ${error.message}`
                }).finally(() => {
                    if (this.$parent) {
                        this.$parent.executeSearchInProgress = false
                        this.$parent.executeSearch()
                    }
                })
            } catch (error) {
                console.error('Error merging incident filters:', error)
                if (this.$parent) {
                    this.$parent.executeSearchInProgress = false
                }
            }
        },

        /**
         * Merge a filter option (gender/ethnicity/disability) with an existing filter of the same type.
         * @param {Object} option - The new option to merge
         * @param {string} filterType - Type of filter ('gender', 'ethnicity', 'disability')
         * @param {Object} config - Configuration for the specific filter type
         * @returns {void}
         */
        mergeFilterOption(option, filterType, config) {
            const existingFilter = this.params.filters.find(f => f.type === filterType)

            if (existingFilter) {
                // Special handling for incident (multiple multiselect fields)
                if (filterType === 'incident' && config.multiFields) {
                    this.mergeIncidentFilterOption(option, existingFilter, config)
                    return
                }

                // Merge: add this selection to the existing filter's arrays
                const itemNameKey = config.itemNameKey || 'display_name'
                const existingValues = existingFilter.editorParams?.[config.editorKey]?.map(item => item[config.valueKey])
                const existingDisplayNames = existingFilter.editorParams?.[config.editorKey]?.map(item => item[itemNameKey])
                const existingDisplayNameGroups = existingFilter.editorParams?.[config.editorKey]?.map(item => item.display_name_group)

                // Build merged display_name_group array
                const newDisplayNameGroups = option.params.display_name_group || []
                const mergedDisplayNameGroups = [...existingDisplayNameGroups, ...newDisplayNameGroups]

                // Only include display_name_group in params if there are any truthy entries
                const hasAnyGroups = mergedDisplayNameGroups.some(g => g)

                const mergedParams = {
                    [config.paramKey]: [
                        ...existingValues,
                        ...option.params[config.paramKey]
                    ],
                    display_name: [
                        ...existingDisplayNames,
                        ...option.params.display_name
                    ]
                }

                if (hasAnyGroups) {
                    mergedParams.display_name_group = mergedDisplayNameGroups
                }

                // Preserve additional params for filters that need them (like disability)
                if (config.preserveParams) {
                    config.preserveParams.forEach(key => {
                        if (existingFilter.params?.[key] !== undefined) {
                            mergedParams[key] = existingFilter.params[key]
                        }
                    })
                }

                const mergedOption = {
                    type: filterType,
                    params: mergedParams
                }

                // Preserve restrict if present
                if (option.restrict !== undefined || existingFilter.restrict !== undefined) {
                    mergedOption.restrict = option.restrict !== undefined ? option.restrict : existingFilter.restrict
                }

                // Apply the merged filter (replaceFilterInState will handle removing the old one)
                if (this.$parent) {
                    this.$parent.executeSearchInProgress = true
                }
                try {
                    // Apply the merged filter
                    this.pushChatMessage({ role: 'assistant', msgType: 'final', content: '' })
                    const msg = this.chatMessages[this.chatMessages.length - 1]
                    this.applyFilterByType(mergedOption, msg).then(filterMessage => {
                        if (filterMessage) {
                            const closingMessage = 'Need changes? Just ask, or use the filter buttons above.'
                            msg.content = `${filterMessage}\n\n${closingMessage}`
                        }
                    }).catch(error => {
                        console.error(`Error applying merged ${filterType} filter:`, error)
                        msg.content = `Error applying filter: ${error.message}`
                    }).finally(() => {
                        // Always clear flag and trigger search
                        if (this.$parent) {
                            this.$parent.executeSearchInProgress = false
                            this.$parent.executeSearch()
                        }
                    })
                } catch (error) {
                    console.error(`Error merging ${filterType} filters:`, error)
                    // Clear flag on sync error
                    if (this.$parent) {
                        this.$parent.executeSearchInProgress = false
                    }
                }
            } else {
                // No existing filter, apply this one directly
                this.pushChatMessage({ role: 'assistant', msgType: 'final', content: '' })
                const msg = this.chatMessages[this.chatMessages.length - 1]
                this.applyFilterByType(option, msg).then(filterMessage => {
                    if (filterMessage) {
                        const closingMessage = 'Need changes? Just ask, or use the filter buttons above.'
                        msg.content = `${filterMessage}\n\n${closingMessage}`
                    }
                }).catch(error => {
                    console.error(`Error applying ${filterType} filter from option:`, error)
                    msg.content = `Error applying filter: ${error.message}`
                })
            }
        },

        /**
         * Toggle attendance option (month/weekday selection).
         * Adds or removes the option's months/weekdays from the existing filter.
         * @param {Object} option - The attendance option with occurrence_month_num and/or occurrence_weekday_num
         */
        toggleAttendanceOption(option) {
            const existingFilter = this.params.filters.find(f => f.type === 'attendance')

            if (!existingFilter) {
                this.applyOptionFilter(option, 'attendance')
                return
            }

            const optionMonths = option.params?.occurrence_month_num || []
            const optionWeekdays = option.params?.occurrence_weekday_num || []

            // Handle month/weekday toggles
            if (optionMonths.length > 0) {
                const existingMonths = existingFilter.params?.occurrence_month_num || []
                const isSelected = optionMonths.every(m => existingMonths.includes(m))
                const updatedMonths = isSelected
                    ? existingMonths.filter(m => !optionMonths.includes(m))
                    : [...new Set([...existingMonths, ...optionMonths])]

                this.applyOptionFilter({
                    type: 'attendance',
                    params: {
                        ...existingFilter.params,
                        occurrence_month_num: updatedMonths,
                    },
                    restrict: existingFilter.restrict
                }, 'attendance')
                return
            } else if (optionWeekdays.length > 0) {
                const existingWeekdays = existingFilter.params?.occurrence_weekday_num || []
                const isSelected = optionWeekdays.every(w => existingWeekdays.includes(w))
                const updatedWeekdays = isSelected
                    ? existingWeekdays.filter(w => !optionWeekdays.includes(w))
                    : [...new Set([...existingWeekdays, ...optionWeekdays])]

                this.applyOptionFilter({
                    type: 'attendance',
                    params: {
                        ...existingFilter.params,
                        occurrence_weekday_num: updatedWeekdays,
                    },
                    restrict: existingFilter.restrict
                }, 'attendance')
                return
            }

            // Handle selection buttons (rate_type or filter_by with default range 0-100)
            const isDefaultRange = option.params?.attendance_value_min === 0 &&
                                  option.params?.attendance_value_max === 100

            if (isDefaultRange) {
                // Handle rate_type selection button
                if (option.params?.rate_type && !option.params?.filter_by) {
                    const isSelected = existingFilter.params?.rate_type === option.params?.rate_type

                    if (isSelected) {
                        // Deselect: revert to default rate_type
                        this.applyOptionFilter({
                            type: 'attendance',
                            params: {
                                ...existingFilter.params,
                                rate_type: 'days_equivalent',
                            },
                            restrict: existingFilter.restrict
                        }, 'attendance')
                    } else {
                        // Select: update to the new rate_type
                        this.applyOptionFilter({
                            type: 'attendance',
                            params: {
                                ...existingFilter.params,
                                rate_type: option.params.rate_type,
                            },
                            restrict: existingFilter.restrict
                        }, 'attendance')
                    }
                    return
                }

                // Handle filter_by selection button
                if (option.params?.filter_by && !option.params?.rate_type) {
                    const isSelected = existingFilter.params?.filter_by === option.params?.filter_by

                    if (isSelected) {
                        // Deselect: revert to default filter_by
                        this.applyOptionFilter({
                            type: 'attendance',
                            params: {
                                ...existingFilter.params,
                                filter_by: 'rate',
                            },
                            restrict: existingFilter.restrict
                        }, 'attendance')
                    } else {
                        // Select: update to the new filter_by
                        this.applyOptionFilter({
                            type: 'attendance',
                            params: {
                                ...existingFilter.params,
                                filter_by: option.params.filter_by,
                            },
                            restrict: existingFilter.restrict
                        }, 'attendance')
                    }
                    return
                }
            }

            // Handle complete attendance filter toggle (specific min/max range)
            if (option.params?.attendance_value_min !== undefined || option.params?.attendance_value_max !== undefined) {
                const isSelected = existingFilter.params?.attendance_value_min === option.params?.attendance_value_min &&
                                 existingFilter.params?.attendance_value_max === option.params?.attendance_value_max

                if (isSelected) {
                    // Remove the filter
                    this.params.filters = this.params.filters.filter(f => f !== existingFilter)
                } else {
                    // Replace with new filter
                    this.applyOptionFilter(option, 'attendance')
                }
                return
            }

            // Fallback: apply as new filter
            this.applyOptionFilter(option, 'attendance')
        },

        /**
         * Apply a filter from an option button click and respond with a chat message.
         * @param {Object} option - The option object containing filter params
         * @param {string} errorContext - Filter type name for error logging (e.g., 'attendance', 'assessment')
         */
        applyOptionFilter(option, errorContext) {
            this.pushChatMessage({ role: 'assistant', msgType: 'final', content: '' })
            const msg = this.chatMessages[this.chatMessages.length - 1]
            this.applyFilterByType(option, msg).then(filterMessage => {
                if (filterMessage) {
                    const closingMessage = 'Need changes? Just ask, or use the filter buttons above.'
                    msg.content = `${filterMessage}\n\n${closingMessage}`
                }
            }).catch(error => {
                console.error(`Error applying ${errorContext} filter from option:`, error)
                msg.content = `Error applying filter: ${error.message}`
            })
        },

        selectOption(option) {
            if (typeof option === 'object' && option !== null) {
                // Handle different option types
                if (option.type) {
                    switch (option.type) {
                        case 'assessment': {
                            if (!option.params) break

                            const { data_point_type, sub_category, data_point_name } = option.params

                            // Check if we have all required fields
                            if (data_point_type && sub_category && data_point_name) {
                                // Toggle: exact match already applied → remove it; otherwise add it
                                // alongside any other assessment filters (additive, same as clicking
                                // multiple different assessments/windows/bands).
                                if (this.findMatchingFilter(option)) {
                                    const removedItems = []
                                    this.removeSpecificFilter(option, removedItems)
                                    this.pushChatMessage({ role: 'assistant', msgType: 'final', content: '' })
                                    const msg = this.chatMessages[this.chatMessages.length - 1]
                                    this.setRemovalMessage(msg, removedItems)
                                } else {
                                    this.applyOptionFilter(option, 'assessment')
                                }
                            } else {
                                // Incomplete assessment - send back to agent to get window options
                                const parts = [
                                    data_point_type?.name,
                                    sub_category?.display_name || sub_category?.name
                                ].filter(Boolean)
                                this.sendMessageFromSidebar(parts.join(' '))
                            }
                            return
                        }

                        case 'attendance': {
                            if (!option.params) break
                            this.toggleAttendanceOption(option)
                            return
                        }

                        case 'demographic': {
                            if (!option.params) break
                            // Doesn't fit the gender/tag/disability/incident merge-toggle below
                            // (that assumes a flat array of values in one filter) — a demographic
                            // is one encoded value per demographic_id. Same shape as assessment:
                            // clicking the applied option removes it, otherwise apply it.
                            // applyDemographicFilter replaces any prior filter for the same
                            // demographic, so clicking "No" while "Yes" is applied switches it.
                            // A field-only button (no value to filter on yet): carry the conversation
                            // on with the field, like an incomplete assessment option does.
                            if (!NlfAgentProcessor.isDemographicOptionComplete(option.params)) {
                                this.sendMessageFromSidebar(`Filter by ${option.params.name}`)
                                return
                            }
                            if (NlfAgentProcessor.isDemographicOptionApplied(this.params.filters, option)) {
                                // Same as the other removal paths: the remembered agent filters no longer match
                                // the screen, and asking for the same demographic again would be skipped as a repeat.
                                this._lastAgentFilters = []
                                const removedItems = []
                                this.removeSpecificFilter(option, removedItems)
                                this.pushChatMessage({ role: 'assistant', msgType: 'final', content: '' })
                                const msg = this.chatMessages[this.chatMessages.length - 1]
                                this.setRemovalMessage(msg, removedItems)
                            } else {
                                this.applyOptionFilter(option, 'demographic')
                            }
                            return
                        }

                        case 'course': {
                            if (!option.params) break
                            // A teacher-only button (from an ambiguous teacher match): carry the
                            // conversation on with the teacher so the agent resolves their courses.
                            if (!NlfAgentProcessor.isCourseOptionComplete(option.params)) {
                                this.sendMessageFromSidebar(`Filter by teacher ${option.params.user_name}`)
                                return
                            }
                            // Checkbox-like within one teacher: clicking an applied option removes its
                            // courses, clicking another adds them. A different teacher replaces the filter.
                            const nextParams = NlfAgentProcessor.toggleCourseOption(this.params.filters, option)
                            if (nextParams === null) {
                                // Same as the other removal paths: the remembered agent filters no longer match
                                // the screen, and asking for the same course again would be skipped as a repeat.
                                this._lastAgentFilters = []
                                const removedItems = []
                                this.removeFiltersByType('course', removedItems)
                                this.pushChatMessage({ role: 'assistant', msgType: 'final', content: '' })
                                const msg = this.chatMessages[this.chatMessages.length - 1]
                                this.setRemovalMessage(msg, removedItems)
                            } else {
                                this.applyOptionFilter({ type: 'course', params: nextParams }, 'course')
                            }
                            return
                        }

                        case 'group_by': {
                            if (!option.params?.value) break

                            this.pushChatMessage({ role: 'assistant', msgType: 'final', content: '' })
                            const msg = this.chatMessages[this.chatMessages.length - 1]
                            const groupByOutcome = this.applyGroupBy(option.params.value)
                            msg.content = groupByOutcome
                                ? (groupByOutcome.suppressClosing
                                    ? groupByOutcome.message
                                    : `${groupByOutcome.message}\n\nNeed changes? Just ask, or use the group-by tabs above.`)
                                : `Sorry, "${option.params.value}" isn't available for grouping right now.`
                            return
                        }

                        case 'gender':
                        case 'ethnicity':
                        case 'disability':
                        case 'meal_status':
                        case 'tag':
                        case 'incident': {
                            if (!option.params) break

                            const configs = {
                                gender: {
                                    editorKey: 'genders',
                                    valueKey: 'value',
                                    paramKey: 'gender'
                                },
                                ethnicity: {
                                    editorKey: 'ethnicities',
                                    valueKey: 'value',
                                    paramKey: 'ethnicity'
                                },
                                disability: {
                                    editorKey: 'disabilities',
                                    valueKey: 'id',
                                    paramKey: 'student_disability_type_id',
                                    preserveParams: ['school_year_id', 'require_all']
                                },
                                meal_status: {
                                    editorKey: 'mealStatuses',
                                    valueKey: 'id',
                                    paramKey: 'meal_status_id',
                                    preserveParams: ['school_year_id']
                                },
                                tag: {
                                    editorKey: 'tags',
                                    valueKey: 'id',
                                    itemNameKey: 'name',
                                    paramKey: 'tag_id',
                                    preserveParams: ['tagged_as_of_date', 'require_all', 'school_year_id']
                                },
                                incident: {
                                    // Incident has multiple multiselect fields, handle each one
                                    multiFields: [
                                        { editorKey: 'incident_behavior_type', valueKey: 'id', paramKey: 'incident_behavior_type_id', nameKey: 'incident_behavior_type_name' },
                                        { editorKey: 'incident_behavior', valueKey: 'id', paramKey: 'incident_behavior_id', nameKey: 'incident_behavior_name' },
                                        { editorKey: 'incident_location', valueKey: 'id', paramKey: 'incident_location_id', nameKey: 'incident_location_name' },
                                        { editorKey: 'incident_response', valueKey: 'id', paramKey: 'incident_response_id', nameKey: 'incident_response_name' }
                                    ],
                                    preserveParams: ['start_date', 'end_date', 'minimum_count', 'maximum_count', 'school_year_id']
                                }
                            }
                            const config = configs[option.type]

                            // Toggle: if already selected, remove it; otherwise add it
                            if (this.isFilterOptionSelected(option, option.type, config)) {
                                this.removeFilterOption(option, option.type, config)
                            } else {
                                this.mergeFilterOption(option, option.type, config)
                            }
                            return
                        }

                        default:
                            console.warn(`Unknown option type: ${option.type}`)
                            break
                    }
                }

                // School and grade options toggle in the search bar's selection, like its multiselect
                if (option.kind === 'school' || option.kind === 'grade') {
                    this.toggleSearchSelection(option)
                    return
                }

                if (option.kind === 'school_year') {
                    this.selectSchoolYear(option)
                    return
                }

                // The agent can send school/grade options without `kind`, so keep echoing the name back
                if (option.id !== undefined && option.name !== undefined) {
                    this.sendMessageFromSidebar(option.name)
                    return
                }
            }

            const content = typeof option === 'object' ? option.label : option
            if (content) {
                this.sendMessageFromSidebar(content)
            }
        },

        toggleSearchSelection(option) {
            const toggled = NlfAgentProcessor.toggleSearchOption(this.$parent?.$refs?.demographicSearch, option)
            if (!toggled) return

            const search = { ...this.params.search }
            if (toggled.ids.length) {
                search[toggled.param] = toggled.ids
            } else {
                delete search[toggled.param]
            }
            this.params.search = search
            // The remembered agent search no longer matches the search bar
            this._lastAgentSearch = null

            this.pushChatMessage({
                role: 'assistant',
                msgType: 'final',
                content: toggled.names.length
                    ? `${toggled.label}: **${toggled.names.join(', ')}**\n\nNeed changes? Just ask, or use the filter buttons above.`
                    : `${toggled.label} cleared.\n\nNeed changes? Just ask, or use the filter buttons above.`,
            })
            this.$parent.executeSearch()
        },

        async selectSchoolYear(option) {
            const selected = await NlfAgentProcessor.selectSchoolYearOption(this.$parent?.$refs?.demographicSearch, option)
            if (!selected) return

            const search = { ...this.params.search, school_year_id: selected.school_year_id }
            if (selected.student_active_date) {
                search.student_active_date = selected.student_active_date
            } else {
                delete search.student_active_date
            }
            this.params.search = search
            // The remembered agent search no longer matches the search bar
            this._lastAgentSearch = null

            this.pushChatMessage({
                role: 'assistant',
                msgType: 'final',
                content: `School Year: **${selected.name}**\n\nNeed changes? Just ask, or use the filter buttons above.`,
            })
            this.$parent.executeSearch()
        },

        resetChat() {
            this.chatMessages = []
            this.chatInput = ''
            this.isThinking = false
            this.isStreaming = false
            this.streamingMessageIndex = null
            this._chatMessageSeq = 0
            this.closeWebSocket()
            this.initializeWebSocket().catch(() => {})

            this.resetFiltersAndGrouping()
        },
    },

    watch: {
        charts(v) {},
        aiModeEnabled(val) {
            if (val) {
                this.initializeWebSocket().catch(() => {})
            } else {
                this.closeWebSocket()
                this.chatMessages = []
                this.chatInput = ''
                this.isThinking = false
                this.isStreaming = false
                this.streamingMessageIndex = null
                this._chatMessageSeq = 0
            }
        },
    },

    data() {
        return {
            rocketImg,
            labelHover: null,
            showGrid: false,
            websocket: null,
            wsConnected: false,
            wsResolve: null,
            wsConnectingPromise: null,
            _chatMessageSeq: 0,
            // Snapshots of the last agent-provided search/filters, so applyAgentResult doesn't
            // reprocess when nothing changed — e.g. just switching the grouping tab.
            _lastAgentSearch: null,
            _lastAgentFilters: [],
        }
    },

    beforeDestroy() {
        this.closeWebSocket()
    },
}
</script>

<style lang="scss" scoped>
    .chips-and-toggle-row .chips-label {
        font-size: 14px;
        color: #050f2d;
    }

    .chips-wrapper {
        flex: 1 1 0;
        min-width: 0;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }

    .chips-and-toggle-row {
        margin-left: 24px !important;
        margin-right: 24px !important;
        margin-top: 24px !important;
    }
    .disabled {
        pointer-events: none;
        opacity: .3;
    }
    .view-toggle-row {
        flex-shrink: 0;
    }
    ::v-deep .students3d-toggle-btn {
        white-space: nowrap;
        .d-flex > div {
            white-space: nowrap;
        }
    }
</style>
